import { instance } from '../api/api';
import { toApiError } from '../utils/apiError';
import { useAppSelector } from '../store/hooks';
import { RootState } from '../store/store';
import { IMedList } from '../types/type';
import { formatDateForApi } from '../utils/date.utils';

export interface IMedicalStandard {
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
}

export interface IMedicLicense {
  issueDate: string;
  number: string;
  profileID: string;
  ser: string;
}

export interface MedListResponse {
  data: IMedList[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface MedListFilters {
  page?: number;
  limit?: number;
  startTime?: string;
  endTime?: string;
  fullName?: string;
  num?: string | number;
  checkStatus?: string;
  autoparkName?: string;
}

export const MedicalService = {
  async getMedicalStandards(): Promise<IMedicalStandard[]> {
    try {
      const { data } = await instance.get<IMedicalStandard[]>('/med-standards');
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить медицинские нормативы');
    }
  },

  async updateMedicalStandard(standard: IMedicalStandard): Promise<IMedicalStandard> {
    try {
      const { data } = await instance.put<IMedicalStandard>(`/med-standards/${standard.id}`, standard);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить медицинский норматив');
    }
  },

  async createMedicLicense(license: IMedicLicense): Promise<any> {
    try {
      const { data } = await instance.post('/medlist_license', license);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось создать лицензию медика');
    }
  },

  async getMedicMedLists(medicID: string, filters: MedListFilters = {}): Promise<MedListResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Копируем фильтры и форматируем даты
      const formattedFilters = { ...filters };

      // Форматируем даты, если они есть
      if (filters.startTime) {
        formattedFilters.startTime = formatDateForApi(filters.startTime);
      }

      if (filters.endTime) {
        formattedFilters.endTime = formatDateForApi(filters.endTime);
      }

      // Добавляем параметры в URL
      Object.entries(formattedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const { data } = await instance.get<MedListResponse>(`/medlist/medic/${medicID}${queryString}`);

      return data;
    } catch (error: any) {
      console.error('Ошибка при получении медицинских осмотров:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить медицинские осмотры медика');
    }
  },

  async getDriverMedLists(driverID: string, filters: MedListFilters = {}): Promise<MedListResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Копируем фильтры и форматируем даты
      const formattedFilters = { ...filters };

      // Форматируем даты, если они есть
      if (filters.startTime) {
        formattedFilters.startTime = formatDateForApi(filters.startTime);
      }

      if (filters.endTime) {
        formattedFilters.endTime = formatDateForApi(filters.endTime);
      }

      // Добавляем параметры в URL
      Object.entries(formattedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const { data } = await instance.get<MedListResponse>(`/medlist/driver/${driverID}${queryString}`);

      return data;
    } catch (error: any) {
      console.error('Ошибка при получении медицинских осмотров водителя:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить медицинские осмотры водителя');
    }
  },

  async updateMedListStatus({
    id,
    status,
    medicID,
    reasonForRefusal,
  }: {
    id: string | number;
    status: string;
    medicID: string;
    reasonForRefusal?: string;
  }): Promise<any> {
    try {
      const { data } = await instance.put(`/medlist/${id}/status`, { status, medicID, reasonForRefusal });
      return data;
    } catch (error: any) {
      console.error('Ошибка при обновлении статуса медицинского осмотра:', error);
      throw toApiError(error, 'Не удалось обновить статус медицинского осмотра');
    }
  },
};
